import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { MemberDto } from '@wingseat/shared';
import { api } from '../lib/api-client';

export const memberKeys = {
  all: ['members'] as const,
  list: () => ['members', 'list'] as const,
};

export function useMembers() {
  return useQuery({
    queryKey: memberKeys.list(),
    queryFn: () => api.get<MemberDto[]>('/members'),
  });
}

export function useToggleFavorite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { userId: string; favorite: boolean }) => {
      if (vars.favorite) {
        await api.post(`/me/favorites/${vars.userId}`);
      } else {
        await api.delete(`/me/favorites/${vars.userId}`);
      }
    },
    onMutate: async (vars) => {
      // Optimistic Update
      await qc.cancelQueries({ queryKey: memberKeys.list() });
      const prev = qc.getQueryData<MemberDto[]>(memberKeys.list());
      if (prev) {
        qc.setQueryData<MemberDto[]>(
          memberKeys.list(),
          prev.map((m) =>
            m.id === vars.userId ? { ...m, isFavorite: vars.favorite } : m,
          ),
        );
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) {
        qc.setQueryData(memberKeys.list(), ctx.prev);
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: memberKeys.all });
    },
  });
}
